import React, { useState, useEffect } from 'react';
import {
  X, Plus, Trash2, Clock, Check, AlertCircle, Copy, QrCode,
  ChevronDown, Phone, Users, ShoppingCart, DollarSign, Calendar,
  Maximize2, Minimize2, Grid, Utensils, Bed, DoorOpen, Lock, Unlock, CalendarClock,
  Search, UserCheck, UserPlus
} from 'lucide-react';
import { api } from '../services/api';
import toast from 'react-hot-toast';

interface RoomItem {
  _id?: string;
  itemId: string;
  name: string;
  price: number;
  qty: number;
  type: 'product' | 'service' | 'bundle';
}

interface PendingRoom {
  _id?: string;
  ticketCode: string;
  clientId?: string;
  clientName: string;
  clientPhone: string;
  items: RoomItem[];
  subtotal: number;
  discount: number;
  total: number;
  createdAt: string;
  status: 'open' | 'paid-full' | 'paid-50' | 'reserved' | 'closed';
  notes?: string;
}

interface DiningRoom {
  _id: string;
  name: string;
  number?: number;
  type: 'table' | 'room';
  status: 'free' | 'busy' | 'reserved';
  peopleCount: number;
  reservedUntil?: string;
  pendingRoomId?: any;
}

interface PendingPaymentRoomsProps {
  onRoomUpdated?: (room: PendingRoom) => void;
  onRoomClosed?: (room: PendingRoom) => void;
  onCountChange?: (count: number) => void;
  showDrawer?: boolean;
  onCloseDrawer?: () => void;
  selectedTableId?: string;
  selectedTableName?: string;
  onSelectTable?: (room: { _id: string; name: string; pendingRoomId?: any } | null) => void;
}

export const PendingPaymentRooms: React.FC<PendingPaymentRoomsProps> = ({
  onRoomUpdated,
  onRoomClosed,
  onCountChange,
  showDrawer = true,
  onCloseDrawer,
  selectedTableId,
  selectedTableName,
  onSelectTable,
}) => {
  const [rooms, setRooms] = useState<PendingRoom[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedRoomId, setExpandedRoomId] = useState<string | null>(null);
  const [selectedRoom, setSelectedRoom] = useState<PendingRoom | null>(null);
  const [editingRoom, setEditingRoom] = useState<PendingRoom | null>(null);

  // ========== EXPANSÃO / MESAS & QUARTOS ==========
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState<'pending' | 'rooms'>('pending');
  const [diningRooms, setDiningRooms] = useState<DiningRoom[]>([]);
  const [roomsLoading, setRoomsLoading] = useState(false);
  const [showAddRoom, setShowAddRoom] = useState(false);
  const [roomForm, setRoomForm] = useState({ name: '', number: '', type: 'table' });
  const [peopleInput, setPeopleInput] = useState<Record<string, string>>({});
  const [reserveUntil, setReserveUntil] = useState<Record<string, string>>({});

  // Cliente na abertura de mesa/quarto (estilo cart)
  const [openForRoom, setOpenForRoom] = useState<DiningRoom | null>(null);
  const [clients, setClients] = useState<any[]>([]);
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [clientSearchResults, setClientSearchResults] = useState<any[]>([]);
  const [showQuickClient, setShowQuickClient] = useState(false);
  const [quickClientName, setQuickClientName] = useState('');
  const [quickClientPhone, setQuickClientPhone] = useState('');

  // Confirmation modals
  const [deleteConfirm, setDeleteConfirm] = useState<{ roomId: string; itemId: string; itemName: string } | null>(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [closeConfirm, setCloseConfirm] = useState<{ roomId: string; status: 'paid-full' | 'paid-50' | 'reserved' } | null>(null);
  const [closeReason, setCloseReason] = useState('');
  const [tempAmountPaid, setTempAmountPaid] = useState<number>(0); // ← NEW: valor recebido agora

  // Carregar rooms ao montar
  useEffect(() => {
    onCountChange?.(rooms.length);
  }, [rooms, onCountChange]);

  const loadRooms = async () => {
    try {
      const response = await api.pendingRooms.getAll('open');
      setRooms(response || []);
    } catch (error) {
      console.error('Erro ao carregar rooms', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDiningRooms = async () => {
    try {
      setRoomsLoading(true);
      const data = await api.rooms.getAll();
      setDiningRooms(data || []);
    } catch (error) {
      console.error('Erro ao carregar mesas/quartos', error);
    } finally {
      setRoomsLoading(false);
    }
  };

  const loadClients = async () => {
    try {
      const res = await api.clients.getAll({ page: 1, limit: 100, search: '', origin: 'all' });
      setClients(res.clients || []);
    } catch (error) {
      console.error('Erro ao carregar clientes', error);
    }
  };

  useEffect(() => {
    loadRooms();
    loadDiningRooms();
    const interval = setInterval(() => {
      loadRooms();
      loadDiningRooms();
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const createRoom = async (clientName: string, clientPhone: string): Promise<PendingRoom | null> => {
    try {
      await api.clients.create({ name: clientName, phone: clientPhone }).catch(() => {});
    } catch (err) {
      console.warn('could not save client while creating room', err);
    }

    try {
      const response = await api.pendingRooms.create({ clientName, clientPhone });
      setRooms(prev => [...prev, response]);
      toast.success(`Room criado: ${response.ticketCode}`);
      return response;
    } catch (error) {
      toast.error('Erro ao criar room');
      return null;
    }
  };

  const addItemToRoom = async (roomId: string, item: any) => {
    try {
      const response = await api.pendingRooms.addItem(roomId, {
        itemId: item._id,
        name: item.name,
        price: item.basePrice || item.price,
        qty: 1,
        type: 'type' in item ? 'bundle' : !('stockQuantity' in item) ? 'service' : 'product',
      });

      const updatedRoom = response;
      setRooms(rooms.map(r => r._id === roomId ? updatedRoom : r));
      setSelectedRoom(updatedRoom);
      if (editingRoom?._id === roomId) setEditingRoom(updatedRoom);
      onRoomUpdated?.(updatedRoom);
      toast.success('Item adicionado ao room');
    } catch (error) {
      toast.error('Erro ao adicionar item');
    }
  };

  const removeItemFromRoom = async (roomId: string, itemId: string, reason?: string) => {
    try {
      const response = await api.pendingRooms.removeItem(roomId, itemId, reason);

      if (response.roomDeleted) {
        setRooms(rooms.filter(r => r._id !== roomId));
        setSelectedRoom(null);
        setEditingRoom(null);
        toast.success(`Room ${response.ticketCode} removido automaticamente (sem itens)`);
      } else {
        const updatedRoom = response;
        setRooms(rooms.map(r => r._id === roomId ? updatedRoom : r));
        setSelectedRoom(updatedRoom);
        if (editingRoom?._id === roomId) setEditingRoom(updatedRoom);
        toast.success('Item removido com sucesso');
      }

      setDeleteConfirm(null);
      setDeleteReason('');
    } catch (error: any) {
      console.error('removeItemFromRoom error:', error);
      toast.error('Erro ao remover item: ' + (error.message || 'Erro desconhecido'));
    }
  };

  const closeRoom = async (
    roomId: string,
    finalStatus: 'paid-full' | 'paid-50' | 'reserved',
    reason?: string,
    amountPaidNow?: number
  ) => {
    try {
      const payload: any = {};

      // Enviar valor recebido apenas quando relevante
      if ((finalStatus === 'paid-50' || finalStatus === 'reserved') && amountPaidNow !== undefined && amountPaidNow > 0) {
        payload.amountPaidNow = amountPaidNow;
      }

      const response = await api.pendingRooms.closeRoom(roomId, finalStatus, reason, payload);
     
      console.log('Room closed → sale created?', response.sale?._id);

      const closedRoom = response.room || response;
       const createdSale = response.sale || response;
      setRooms(rooms.filter(r => r._id !== roomId));
      setSelectedRoom(null);
      setEditingRoom(null);
      setCloseConfirm(null);
      setCloseReason('');
      setTempAmountPaid(0); // resetar valor

      onRoomClosed?.(closedRoom);
      onRoomClosed?.(closedRoom, createdSale);
      toast.success(`Room ${closedRoom.ticketCode} fechado com sucesso`);

      if (createdSale?._id) {
        toast('Nova venda registada no histórico!', { icon: '📊' });
      }
    } catch (error: any) {
      console.error('Erro ao fechar room:', error);
      toast.error('Erro ao fechar room: ' + (error.message || 'Tente novamente'));
    }
  };

  const copyTicketToClipboard = (ticketCode: string) => {
    navigator.clipboard.writeText(ticketCode);
    toast.success('Ticket copiado!');
  };

  // ==================== MESAS & QUARTOS ====================
  const handleAddRoom = async () => {
    if (!roomForm.name.trim()) {
      toast.error('Informe o nome da mesa/quarto');
      return;
    }
    try {
      await api.rooms.create({
        name: roomForm.name.trim(),
        number: roomForm.number ? Number(roomForm.number) : undefined,
        type: roomForm.type as 'table' | 'room',
      });
      toast.success('Mesa/quarto criada');
      setRoomForm({ name: '', number: '', type: 'table' });
      setShowAddRoom(false);
      loadDiningRooms();
    } catch (error: any) {
      toast.error(error?.error || error?.message || 'Erro ao criar mesa/quarto');
    }
  };

  const openRoomWithClient = async (room: DiningRoom, clientName: string, clientPhone: string, clientId?: string) => {
    try {
      // Persistir o cliente se for novo (para ter clientId real)
      let resolvedClientId = clientId;
      if (!resolvedClientId && clientName.trim()) {
        try {
          const existing = clients.find(
            (c: any) => c.name === clientName.trim() || (clientPhone && c.phone === clientPhone)
          );
          if (existing) {
            resolvedClientId = existing._id;
          } else {
            const newClient = await api.clients.create({ name: clientName.trim(), phone: clientPhone || undefined });
            resolvedClientId = newClient?._id;
            if (newClient) setClients(prev => [...prev, newClient]);
          }
        } catch (err) {
          console.warn('could not save client while opening room', err);
        }
      }

      const people = peopleInput[room._id] ? Number(peopleInput[room._id]) : 0;
      const res = await api.rooms.open(room._id, {
        peopleCount: people > 0 ? people : undefined,
        clientName: clientName || room.name,
        clientPhone: clientPhone || undefined,
        clientId: resolvedClientId || undefined,
      });
      setDiningRooms(diningRooms.map(r => (r._id === room._id ? { ...r, ...res.room, pendingRoomId: res.pendingRoom?._id } : r)));
      if (res.pendingRoom?._id) {
        loadRooms();
      }
      // Abrir uma mesa também a seleciona (vendas vão para lá)
      onSelectTable?.({ _id: room._id, name: room.name, pendingRoomId: res.pendingRoom?._id || room.pendingRoomId?._id || null });
      toast.success(`${room.name} aberta para ${clientName}`);
      return true;
    } catch (error: any) {
      // Estado desatualizado → recarregar para ressincronizar antes de falhar
      try {
        const fresh = await api.rooms.getAll();
        setDiningRooms(fresh || []);
        const freshRoom = (fresh || []).find(r => r._id === room._id);
        if (freshRoom && freshRoom.status !== 'free') {
          onSelectTable?.({
            _id: room._id,
            name: room.name,
            pendingRoomId: freshRoom.pendingRoomId?._id || freshRoom.pendingRoomId || null,
          });
          toast.success(`Mesa ${room.name} selecionada - vendas vão para esta mesa`);
          return true;
        }
      } catch (refreshError) {
        console.error('Erro ao recarregar mesas após falha', refreshError);
      }
      toast.error(error?.error || error?.message || 'Erro ao abrir mesa/quarto');
      return false;
    }
  };

  const handleOpenRoom = async (room: DiningRoom) => {
    setOpenForRoom(room);
    setClientSearchTerm('');
    setClientSearchResults([]);
    setShowQuickClient(false);
    setQuickClientName('');
    setQuickClientPhone('');
    loadClients();
  };

  const handleReserveRoom = async (room: DiningRoom) => {
    try {
      const until = reserveUntil[room._id];
      await api.rooms.reserve(room._id, { reservedUntil: until || undefined });
      toast.success(`${room.name} reservada`);
      setReserveUntil(prev => ({ ...prev, [room._id]: '' }));
      loadDiningRooms();
    } catch (error: any) {
      toast.error(error?.error || 'Erro ao reservar mesa/quarto');
    }
  };

  const handleFreeRoom = async (room: DiningRoom) => {
    try {
      await api.rooms.free(room._id);
      toast.success(`${room.name} libertada`);
      setDiningRooms(diningRooms.map(r => (r._id === room._id ? { ...r, status: 'free', peopleCount: 0, pendingRoomId: null } : r)));
      if (selectedTableId === room._id) onSelectTable?.(null);
      loadRooms();
    } catch (error: any) {
      toast.error(error?.error || 'Erro ao libertar mesa/quarto');
    }
  };

  const handleDeleteRoom = async (room: DiningRoom) => {
    if (!window.confirm(`Eliminar "${room.name}" definitivamente?`)) return;
    try {
      await api.rooms.delete(room._id);
      toast.success(`${room.name} eliminada`);
      setDiningRooms(diningRooms.filter(r => r._id !== room._id));
      if (selectedTableId === room._id) onSelectTable?.(null);
    } catch (error: any) {
      toast.error(error?.error || 'Erro ao eliminar mesa/quarto');
    }
  };

  // Selecionar mesa/quarto → todas as vendas seguintes vão para esta mesa
  const handleSelectTable = async (room: DiningRoom) => {
    // Já selecionada → desselecionar
    if (selectedTableId === room._id) {
      onSelectTable?.(null);
      toast.success(`Mesa ${room.name} desselecionada`);
      return;
    }

    // Se estiver livre, pede o cliente primeiro (abre com o cliente associado)
    if (room.status === 'free') {
      setOpenForRoom(room);
      setClientSearchTerm('');
      setClientSearchResults([]);
      setShowQuickClient(false);
      setQuickClientName('');
      setQuickClientPhone('');
      loadClients();
      return;
    }

    // Se estiver ocupada ou reservada, apenas seleciona
    onSelectTable?.({
      _id: room._id,
      name: room.name,
      pendingRoomId: room.pendingRoomId?._id || room.pendingRoomId || null,
    });
    toast.success(`Mesa ${room.name} selecionada - vendas vão para esta mesa`);
  };

  const formatReserved = (d?: string) => {
    if (!d) return '';
    return new Date(d).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' });
  };

  if (!showDrawer) return null;

  return (
    <div className={`fixed bottom-0 right-0 z-40 bg-white shadow-2xl rounded-t-md overflow-hidden flex flex-col transition-all duration-300 ${
      isExpanded ? 'inset-0 sm:inset-3 sm:rounded-2xl' : 'max-h-[90vh] w-full sm:w-[500px]'
    }`}>
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-4 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <Clock size={24} className="text-white" />
          <div>
            <h3 className="text-white font-black uppercase tracking-tight">Aguardando Pagamento</h3>
            <p className="text-sm text-orange-100">{rooms.length} room(s) aberto(s)</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition"
            title={isExpanded ? 'Minimizar' : 'Expandir para ecrã inteiro'}
          >
            {isExpanded ? <Minimize2 className="text-white" size={20} /> : <Maximize2 className="text-white" size={20} />}
          </button>
          <button
            onClick={onCloseDrawer}
            className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition"
          >
            <X className="text-white" size={20} />
          </button>
        </div>
      </div>

      {/* Indicador da mesa selecionada */}
      {selectedTableId && (
        <div className="px-4 py-2 bg-indigo-600 text-white text-xs font-black uppercase flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Grid size={14} /> Mesa selecionada: {selectedTableName || selectedTableId}
          </span>
          <button
            onClick={() => onSelectTable?.(null)}
            className="p-1 hover:bg-white/20 rounded transition"
            title="Desselecionar mesa"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* Tabs: Pagamentos / Mesas & Quartos */}
      <div className="flex border-b border-gray-200">
        <button
          onClick={() => setActiveTab('pending')}
          className={`flex-1 flex items-center justify-center gap-2 py-3 text-xs font-black uppercase transition ${
            activeTab === 'pending' ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50' : 'text-gray-400 hover:text-gray-600'
          }`}
        >
          <ShoppingCart size={14} /> Pagamentos ({rooms.length})
        </button>
        <button
          onClick={() => { setActiveTab('rooms'); loadDiningRooms(); }}
          className={`flex-1 flex items-center justify-center gap-2 py-3 text-xs font-black uppercase transition ${
            activeTab === 'rooms' ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50' : 'text-gray-400 hover:text-gray-600'
          }`}
        >
          <Grid size={14} /> Mesas & Quartos ({diningRooms.length})
        </button>
      </div>

      {activeTab === 'rooms' && (
        <div className="flex-1 overflow-y-auto custom-scrollbar p-3">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-xs font-black text-gray-500 uppercase">Estado das mesas/quartos</p>
              <p className="text-[9px] text-gray-400 font-medium">Clique numa mesa para a selecionar — as vendas vão para essa mesa</p>
            </div>
            <button
              onClick={() => setShowAddRoom(!showAddRoom)}
              className="flex items-center gap-1 px-3 py-2 bg-green-500 text-white text-xs font-black rounded-lg hover:bg-green-600 transition uppercase"
            >
              <Plus size={13} /> Nova Mesa
            </button>
          </div>

          {showAddRoom && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[10px] font-black text-gray-600 uppercase mb-1">Nome</label>
                  <input
                    type="text"
                    placeholder="Ex: Mesa 1 / Quarto 3"
                    value={roomForm.name}
                    onChange={e => setRoomForm({ ...roomForm, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-black text-gray-600 uppercase mb-1">Número</label>
                  <input
                    type="number"
                    placeholder="Opcional"
                    value={roomForm.number}
                    onChange={e => setRoomForm({ ...roomForm, number: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-black text-gray-600 uppercase mb-1">Tipo</label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setRoomForm({ ...roomForm, type: 'table' })}
                    className={`flex-1 flex items-center justify-center gap-1 py-2 text-xs font-bold rounded-lg border transition ${
                      roomForm.type === 'table' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-300'
                    }`}
                  >
                    <Utensils size={13} /> Mesa
                  </button>
                  <button
                    type="button"
                    onClick={() => setRoomForm({ ...roomForm, type: 'room' })}
                    className={`flex-1 flex items-center justify-center gap-1 py-2 text-xs font-bold rounded-lg border transition ${
                      roomForm.type === 'room' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-300'
                    }`}
                  >
                    <Bed size={13} /> Quarto
                  </button>
                </div>
              </div>
              <button
                onClick={handleAddRoom}
                className="w-full py-2 bg-green-600 text-white text-xs font-black rounded-lg hover:bg-green-700 transition uppercase"
              >
                Criar Mesa/Quarto
              </button>
            </div>
          )}

          {roomsLoading ? (
            <div className="p-4 text-center text-gray-400">Carregando...</div>
          ) : diningRooms.length === 0 ? (
            <div className="p-6 text-center">
              <Utensils size={36} className="text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500 font-bold">Nenhuma mesa/quarto criada</p>
              <p className="text-xs text-gray-400">Crie mesas para restaurantes ou quartos para lodges/hotéis.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
              {diningRooms.map(r => {
                const isBusy = r.status === 'busy';
                const isReserved = r.status === 'reserved';
                const isSelected = selectedTableId === r._id;
                return (
                  <div
                    key={r._id}
                    onClick={() => handleSelectTable(r)}
                    className={`relative border-2 rounded-xl p-3 transition cursor-pointer ${
                      isSelected
                        ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-300'
                        : isBusy
                        ? 'border-red-300 bg-red-50'
                        : isReserved
                        ? 'border-amber-300 bg-amber-50'
                        : 'border-green-300 bg-green-50'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-1">
                      <div className="flex items-center gap-1.5">
                        {r.type === 'room' ? <Bed size={15} className="text-gray-600" /> : <Utensils size={15} className="text-gray-600" />}
                        <span className="font-black text-sm text-gray-900">{r.name}</span>
                        {r.number != null && <span className="text-[9px] font-bold text-gray-400">#{r.number}</span>}
                      </div>
                      <div className="flex items-center gap-1">
                        {isSelected && (
                          <span className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-indigo-600 text-white text-[8px] font-black uppercase">
                            <Check size={9} /> Selecionada
                          </span>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDeleteRoom(r); }}
                          className="p-0.5 text-red-400 hover:text-red-600 transition"
                          title="Eliminar"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>

                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-black uppercase border ${
                      isBusy ? 'bg-red-100 text-red-700 border-red-300' : isReserved ? 'bg-amber-100 text-amber-700 border-amber-300' : 'bg-green-100 text-green-700 border-green-300'
                    }`}>
                      {isBusy ? <><DoorOpen size={10} /> Ocupada</> : isReserved ? <><Lock size={10} /> Reservada</> : <><Unlock size={10} /> Livre</>}
                    </span>
                    {isReserved && r.reservedUntil && (
                      <p className="text-[10px] font-bold text-amber-700 mt-1 flex items-center gap-1">
                        <Clock size={10} /> {formatReserved(r.reservedUntil)}
                      </p>
                    )}
                    {isBusy && r.peopleCount > 0 && (
                      <p className="text-[10px] font-bold text-red-700 mt-1 flex items-center gap-1">
                        <Users size={10} /> {r.peopleCount} pessoa(s)
                      </p>
                    )}
                    {isBusy && r.pendingRoomId && (
                      <p className="text-[10px] font-bold text-gray-500 mt-0.5 truncate">
                        {r.pendingRoomId.ticketCode || r.pendingRoomId.name || ''}
                      </p>
                    )}

                    <div className="mt-2 space-y-1">
                      {isBusy ? (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleFreeRoom(r); }}
                          className="w-full py-1.5 bg-red-500 text-white text-[10px] font-black rounded-lg hover:bg-red-600 transition uppercase"
                        >
                          Libertada
                        </button>
                      ) : (
                        <>
                          <div className="flex items-center gap-1">
                            <Users size={11} className="text-gray-500 flex-shrink-0" />
                            <input
                              type="number"
                              min="0"
                              placeholder="Pessoas"
                              value={peopleInput[r._id] ?? ''}
                              onChange={e => { e.stopPropagation(); setPeopleInput(prev => ({ ...prev, [r._id]: e.target.value })); }}
                              className="w-full px-1.5 py-1 border border-gray-300 rounded text-[10px] focus:ring-1 focus:ring-blue-500 outline-none"
                            />
                          </div>
                          <div className="flex gap-1">
                            <button
                              onClick={(e) => { e.stopPropagation(); handleOpenRoom(r); }}
                              className="flex-1 py-1.5 bg-green-600 text-white text-[10px] font-black rounded-lg hover:bg-green-700 transition uppercase"
                            >
                              Abrir
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleReserveRoom(r); }}
                              className="flex-1 py-1.5 bg-amber-500 text-white text-[10px] font-black rounded-lg hover:bg-amber-600 transition uppercase"
                            >
                              Reservar
                            </button>
                          </div>
                          <input
                            type="datetime-local"
                            value={reserveUntil[r._id] ?? ''}
                            onChange={e => { e.stopPropagation(); setReserveUntil(prev => ({ ...prev, [r._id]: e.target.value })); }}
                            className="w-full px-1.5 py-1 border border-gray-300 rounded text-[10px] focus:ring-1 focus:ring-blue-500 outline-none"
                            title="Hora da reserva"
                          />
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Lista de Rooms */}
      {activeTab === 'pending' && (
      <div className="flex-1 overflow-y-auto custom-scrollbar">
        {loading ? (
          <div className="p-4 text-center text-gray-400">Carregando...</div>
        ) : rooms.length === 0 ? (
          <div className="p-6 text-center">
            <AlertCircle size={40} className="text-gray-300 mx-auto mb-2" />
            <p className="text-gray-500 font-bold">Nenhum room aberto</p>
          </div>
        ) : (
          <div className="space-y-2 p-3">
            {rooms.map(room => (
              <div
                key={room._id}
                className={`border-2 rounded-lg p-3 cursor-pointer transition ${
                  selectedRoom?._id === room._id
                    ? 'border-orange-500 bg-orange-50'
                    : 'border-gray-200 hover:border-orange-300'
                }`}
                onClick={() => {
                  setSelectedRoom(room);
                  setExpandedRoomId(prev => (prev === room._id ? null : room._id));
                }}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-black bg-orange-100 text-orange-700 px-2 py-1 rounded">
                        {room.ticketCode}
                      </span>
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          copyTicketToClipboard(room.ticketCode);
                        }}
                        className="p-1 hover:bg-gray-100 rounded transition"
                        title="Copiar ticket"
                      >
                        <Copy size={14} className="text-gray-500" />
                      </button>
                    </div>
                    <p className="text-sm font-bold text-gray-800">{room.clientName}</p>
                    <p className="text-xs text-gray-500 flex items-center gap-1">
                      <Phone size={12} /> {room.clientPhone}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {room.items.length} item(ns) • {room.total.toLocaleString()} MT
                    </p>
                  </div>
                  <ChevronDown
                    size={18}
                    className={`transition ${expandedRoomId === room._id ? 'rotate-180' : ''}`}
                  />
                </div>

                {expandedRoomId === room._id && (
                  <div className="mt-3 pt-3 border-t border-gray-200 space-y-2">
                    {room.items.map(item => (
                      <div key={item._id} className="flex justify-between items-center text-xs bg-gray-50 p-2 rounded">
                        <div>
                          <p className="font-bold text-gray-700">{item.name}</p>
                          <p className="text-gray-500">qty: {item.qty}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-gray-900">{(item.price * item.qty).toLocaleString()} MT</p>
                          <button
                            onClick={e => {
                              e.stopPropagation();
                              setDeleteConfirm({ roomId: room._id!, itemId: item._id!, itemName: item.name });
                              setDeleteReason('');
                            }}
                            className="text-red-500 hover:text-red-700 mt-1"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      )}

      {/* Painel de Ações */}
      {activeTab === 'pending' && selectedRoom && (
        <div className="border-t border-gray-200 p-4 bg-gray-50 space-y-3">
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <p className="text-gray-600 uppercase font-bold">Subtotal</p>
              <p className="font-black text-lg">{selectedRoom.subtotal.toLocaleString()} MT</p>
            </div>
            <div>
              <p className="text-gray-600 uppercase font-bold">TOTAL</p>
              <p className="font-black text-lg text-orange-600">{selectedRoom.total.toLocaleString()} MT</p>
            </div>
          </div>

          <div className="space-y-2">
            <p className="text-xs font-black text-gray-600 uppercase">Fechar Room Como:</p>
            <div className="grid grid-cols-3 gap-2">
              <button
                onClick={() => {
                  setCloseConfirm({ roomId: selectedRoom._id!, status: 'paid-full' });
                  setCloseReason('');
                  setTempAmountPaid(0);
                }}
                className="py-2 bg-green-600 hover:bg-green-700 text-white text-xs font-black rounded-md transition uppercase"
              >
                ✅ Pago 100%
              </button>
              <button
                onClick={() => {
                  setCloseConfirm({ roomId: selectedRoom._id!, status: 'paid-50' });
                  setCloseReason('');
                  setTempAmountPaid(0);
                }}
                className="py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-black rounded-md transition uppercase"
              >
                ⏳ Pagar 50%
              </button>
              <button
                onClick={() => {
                  setCloseConfirm({ roomId: selectedRoom._id!, status: 'reserved' });
                  setCloseReason('');
                  setTempAmountPaid(0);
                }}
                className="py-2 bg-red-500 hover:bg-red-700 text-white text-xs font-black rounded-md transition uppercase"
              >
                📦 Reserva
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Selecionar Cliente (abrir mesa/quarto) */}
      {openForRoom && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                {openForRoom.type === 'room' ? <Bed size={22} className="text-blue-600" /> : <Utensils size={22} className="text-blue-600" />}
                <div>
                  <h3 className="font-black text-gray-900 uppercase">Abrir {openForRoom.name}</h3>
                  <p className="text-xs text-gray-500">Associe um cliente a esta mesa/quarto</p>
                </div>
              </div>
              <button
                onClick={() => setOpenForRoom(null)}
                className="p-1 hover:bg-gray-100 rounded transition"
              >
                <X size={18} />
              </button>
            </div>

            {showQuickClient ? (
              <div className="space-y-2 animate-in slide-in-from-top-2 duration-300">
                <input
                  autoFocus
                  placeholder="Nome Completo"
                  className="w-full px-4 py-3 text-xs rounded-md border border-gray-200 outline-none focus:ring-2 focus:ring-blue-500 font-bold"
                  value={quickClientName}
                  onChange={(e) => setQuickClientName(e.target.value)}
                />
                <input
                  type="tel"
                  placeholder="Telefone/Telemóvel"
                  className="w-full px-4 py-3 text-xs rounded-md border border-gray-200 outline-none focus:ring-2 focus:ring-blue-500 font-bold"
                  value={quickClientPhone}
                  onChange={(e) => setQuickClientPhone(e.target.value)}
                />
                <div className="flex gap-2 pt-1">
                  <button
                    onClick={() => setShowQuickClient(false)}
                    className="flex-1 py-2 bg-gray-200 text-gray-600 text-[10px] font-black rounded-md uppercase"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={() => {
                      setShowQuickClient(false);
                      openRoomWithClient(openForRoom, quickClientName.trim(), quickClientPhone.trim());
                      setOpenForRoom(null);
                    }}
                    className="flex-[2] py-2 bg-blue-600 text-white text-[10px] font-black rounded-md uppercase shadow-md shadow-blue-100"
                  >
                    Abrir com este Cliente
                  </button>
                </div>
              </div>
            ) : (
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                <input
                  placeholder="Pesquisar cliente por nome ou telefone..."
                  className="w-full pl-11 pr-4 py-3 text-xs rounded-md border border-gray-200 outline-none focus:ring-2 focus:ring-blue-500 font-bold"
                  value={clientSearchTerm}
                  onChange={(e) => {
                    const term = e.target.value.trim();
                    setClientSearchTerm(term);
                    if (term.length > 0 && Array.isArray(clients)) {
                      const filtered = clients.filter((c: any) => {
                        if (!c) return false;
                        const nameMatch = c.name ? c.name.toLowerCase().includes(term.toLowerCase()) : false;
                        const phoneMatch = c.phone ? c.phone.toString().includes(term) : false;
                        return nameMatch || phoneMatch;
                      });
                      setClientSearchResults(filtered);
                    } else {
                      setClientSearchResults([]);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      setClientSearchTerm('');
                      setClientSearchResults([]);
                    }
                  }}
                  autoComplete="off"
                />

                {clientSearchTerm.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-md shadow-xl border border-gray-100 z-50 max-h-60 overflow-y-auto p-2">
                    {clientSearchResults.length > 0 ? (
                      clientSearchResults.map((client: any) => (
                        <button
                          key={client._id}
                          onClick={() => {
                            openRoomWithClient(openForRoom, client.name, client.phone, client._id);
                            setOpenForRoom(null);
                          }}
                          className="w-full flex items-center justify-between p-3 hover:bg-blue-50 rounded-md transition-colors text-left"
                        >
                          <div>
                            <p className="text-[11px] font-black uppercase text-gray-800">{client.name}</p>
                            <p className="text-[9px] text-gray-500">{client.phone || 'Sem telefone'}</p>
                          </div>
                          <UserCheck size={14} className="text-blue-500" />
                        </button>
                      ))
                    ) : (
                      <button
                        onClick={() => {
                          setQuickClientName(clientSearchTerm);
                          setShowQuickClient(true);
                          setClientSearchResults([]);
                        }}
                        className="w-full p-3 flex items-center gap-3 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                      >
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <UserPlus size={16} />
                        </div>
                        <div className="text-left">
                          <p className="text-[11px] font-black uppercase">Criar novo cliente "{clientSearchTerm}"</p>
                          <p className="text-[9px] opacity-70">Clique para registar</p>
                        </div>
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal Remover Item */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <AlertCircle size={24} className="text-red-600" />
              <h3 className="font-black text-gray-900 uppercase">Confirmar Remoção</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Tem a certeza que deseja remover <span className="font-bold">{deleteConfirm.itemName}</span> do room?
            </p>
            <div className="mb-4">
              <label className="text-xs font-black text-gray-600 uppercase block mb-2">Motivo da Remoção</label>
              <input
                type="text"
                placeholder="Ex: Cliente mudou de ideia, produto fora de stock..."
                value={deleteReason}
                onChange={e => setDeleteReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-red-500 outline-none"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold rounded-md transition uppercase text-xs"
              >
                Cancelar
              </button>
              <button
                onClick={() => removeItemFromRoom(deleteConfirm.roomId, deleteConfirm.itemId, deleteReason)}
                className="flex-1 py-2 bg-red-600 hover:bg-red-700 text-white font-black rounded-md transition uppercase text-xs"
              >
                Remover Item
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Fechar Room – com input para valor parcial */}
      {closeConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <Check size={24} className="text-orange-600" />
              <h3 className="font-black text-gray-900 uppercase">Confirmar Fechamento</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Deseja fechar este room como{' '}
              <span className="font-bold">
                {closeConfirm.status === 'paid-full'
                  ? '✅ Pago 100%'
                  : closeConfirm.status === 'paid-50'
                  ? '⏳ Pago 50%'
                  : '📦 Reserva'}
              </span>
              ?
            </p>

            {/* Input de valor recebido – só aparece para paid-50 e reserved */}
            {(closeConfirm.status === 'paid-50' || closeConfirm.status === 'reserved') && (
              <div className="mb-6">
                <label className="text-xs font-black text-gray-700 uppercase block mb-2">
                  Valor recebido agora (MT)
                </label>
                <div className="relative">
                  <DollarSign size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={tempAmountPaid || ''}
                    onChange={e => setTempAmountPaid(Number(e.target.value) || 0)}
                    placeholder="0"
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md text-base font-bold focus:ring-2 focus:ring-orange-500 outline-none"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Faltam: {(selectedRoom?.total - tempAmountPaid || 0).toLocaleString()} MT
                </p>
              </div>
            )}

            <div className="mb-4">
              <label className="text-xs font-black text-gray-600 uppercase block mb-2">Notas (Opcional)</label>
              <textarea
                placeholder="Ex: O cliente voltará amanhã para pagar o resto..."
                value={closeReason}
                onChange={e => setCloseReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-orange-500 outline-none h-20 resize-none"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setCloseConfirm(null);
                  setTempAmountPaid(0);
                }}
                className="flex-1 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold rounded-lg transition uppercase text-sm"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  closeRoom(
                    closeConfirm.roomId,
                    closeConfirm.status,
                    closeReason,
                    tempAmountPaid
                  );
                }}
                className={`flex-1 py-3 text-white font-black rounded-lg transition uppercase text-sm ${
                  closeConfirm.status === 'paid-full'
                    ? 'bg-green-600 hover:bg-green-700'
                    : closeConfirm.status === 'paid-50'
                    ? 'bg-blue-600 hover:bg-blue-700'
                    : 'bg-amber-600 hover:bg-amber-700'
                }`}
              >
                Confirmar Fechamento
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};